import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as NotebookActions from './notebook.actions';
import { NotebookClientService } from '@mode/notebooks/data-access';
import { NotebookStateService } from '../notebook-state.service';
import { AccountFacade } from '@mode/accounts/contract';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFacade } from '@mode/reports/domain';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { ToastService } from '@mode/shared/util-toast';
import { ImportNotebookSocketSuccessPayload, NotebookFacade } from '@mode/notebooks/contract';
import { SocketSubscription, PusherEvent } from '@mode/shared/data-access-socket';
@Injectable()
export class NotebookEffects {
  createNotebook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookActions.createNotebook),
      mergeMap((action) =>
        this.notebookService.createNotebook(action.reportToken).pipe(
          map((res) => {
            return NotebookActions.addNotebookResourcesOnSuccess({ notebookResource: res });
          }),
          catchError((error) => of(NotebookActions.addNotebookFailure({ reportToken: action.reportToken, error })))
        )
      )
    )
  );

  refreshSessionReportRun$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookActions.refreshSessionReportRun),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.notebookFacade.selectedReportReportRunStateSelector$,
        this.notebookFacade.selectedNotebookSession$,
        this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1)
      ),
      filter(([, , , , , migrationFlagEnable]) => migrationFlagEnable),
      switchMap(([action, account, reportToken, runState, session, _migrationFlagEnable]) => {
        return this.notebookService
          .loadReportRun(account.username, reportToken, action.notebookToken, session.token, runState.token)
          .pipe(
            switchMap(() => EMPTY), // Don't dispatch any action on success
            catchError((err) =>
              of(
                NotebookActions.terminated({
                  terminationReason: err.message,
                  sessionToken: session.token,
                  notebookToken: action.notebookToken,
                })
              )
            )
          );
      })
    )
  );

  clearAllCellOutputs$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookActions.clearAllCellOutputs),
        tap(() => {
          this.notebookStateService.notifyOutputCleared();
        })
      ),
    { dispatch: false }
  );

  // Handle modern Angular run actions
  handleModernRun$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookActions.run),
        tap((action) => {
          if (action.runAllCells) {
            // Run all cells - show all outputs
            this.notebookStateService.resetAllOutputCleared();
          } else if (action.cellTokensToRun && action.cellTokensToRun.length === 1) {
            // Single cell run - show only that cell's output
            const cellToken = action.cellTokensToRun[0];
            this.notebookStateService.resetCellOutputCleared(cellToken);
          }
        })
      ),
    { dispatch: false }
  );
  cancelRun$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookActions.cancelRun),
        withLatestFrom(
          this.accountFacade.currentOrg$,
          this.reportFacade.headerReportToken$,
          this.notebookFacade.selectedNotebookSession$,
          this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1)
        ),
        filter(([action, organization, reportToken, session, isFlagEnabled]) => isFlagEnabled),
        switchMap(([action, organization, reportToken, session]) => {
          const sessionToken = session?.token;
          if (!sessionToken) {
            return of({});
          }
          return this.notebookService.cancelRun(organization.username, reportToken, action.notebookToken, sessionToken);
        })
      ),
    { dispatch: false }
  );

  importNotebook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookActions.importNotebook),
      withLatestFrom(this.accountFacade.currentOrg$, this.reportFacade.headerReportToken$),
      mergeMap(([action, organization, reportToken]) =>
        this.notebookService
          .importNotebook({ file: action.file, orgId: organization.username, reportToken: reportToken ?? '' })
          .pipe(
            map((response) => {
              return NotebookActions.initiateSocketConnection({
                pusherChannelName: response.pusher_channel_name,
              });
            }),
            catchError((error) => {
              return of(
                NotebookActions.importNotebookFailure({
                  error: error?.error?.message || 'Import failed',
                  message: error?.error?.message || 'Failed to import notebook file',
                })
              );
            })
          )
      )
    )
  );

  initiateSocketConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookActions.initiateSocketConnection),
      mergeMap(({ pusherChannelName }) => {
        let importNotebookSubscription: SocketSubscription<ImportNotebookSocketSuccessPayload>;
        try {
          importNotebookSubscription = this.notebookService.connectToImportNotebookSocket(pusherChannelName ?? '');
        } catch (error: unknown) {
          return of(); // Optionally dispatch a failure action
        }
        return importNotebookSubscription.eventBus.pipe(
          filter((event: PusherEvent<any>) => event.name === 'import_completed' || event.name === 'import_failed'),
          map((event: PusherEvent<any>) => {
            if (event.name === 'import_completed') {
              importNotebookSubscription.disconnect();
              return NotebookActions.importNotebookCompleted({
                token: event.data?.token,
                message: event.data?.message,
                state: event.data?.state,
                updated_at: event.data?.updated_at,
                link: event.data?._links?.notebook?.href,
              });
            } else if (event.name === 'import_failed') {
              importNotebookSubscription.disconnect();
              return NotebookActions.importNotebookFailed({
                token: event.data?.token,
                message: event.data?.message,
                state: event.data?.state,
                updated_at: event.data?.updated_at,
              });
            }
            return { type: '[Notebook] Noop' };
          })
        );
      })
    )
  );

  importNotebookCompleted$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookActions.importNotebookCompleted),
        withLatestFrom(this.reportFacade.headerReportToken$),
        tap(() => {
          this.toastService.success('Successfully uploaded Notebook.');
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private notebookService: NotebookClientService,
    private notebookStateService: NotebookStateService,
    private accountFacade: AccountFacade,
    private reportFacade: ReportFacade,
    private notebookFacade: NotebookFacade,
    private featureFlagFacade: FeatureFlagsFacade,
    private toastService: ToastService
  ) {}
}
